import { lazy, Suspense } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import { SkipLink } from './components/SkipLink';
import './styles/fullcalendar.css';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { useMetrics } from './lib/hooks/useMetrics';
import ScrollToTop from './components/ScrollToTop';
import PageTransition from './components/PageTransition';
import OpenAIChatWidget from './components/chat/OpenAIChatWidget';

const AbandonedCartBanner = lazy(() => import('./components/AbandonedCartBanner'));

const Home = lazy(() => import('./pages/Home'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const NutritionPrograms = lazy(() => import('./pages/NutritionPrograms'));
const Programs = lazy(() => import('./pages/Programs'));
const MonthlyApp = lazy(() => import('./pages/MonthlyApp'));
const StandalonePrograms = lazy(() => import('./pages/StandalonePrograms'));
const Shop = lazy(() => import('./pages/Shop'));
const Community = lazy(() => import('./pages/Community'));
const ShredWaitlist = lazy(() => import('./pages/ShredWaitlist'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const Contact = lazy(() => import('./pages/Contact'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminLogin = lazy(() => import('./pages/admin/Login'));
const Checkout = lazy(() => import('./pages/Checkout'));
const CheckoutSuccess = lazy(() => import('./pages/CheckoutSuccess'));
const RedeemGift = lazy(() => import('./pages/RedeemGift'));
const Auth = lazy(() => import('./pages/Auth'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const Subscribe = lazy(() => import('./pages/Subscribe'));
const NotFound = lazy(() => import('./pages/NotFound'));

function App() {
  useMetrics();
  const location = useLocation();

  return (
    <HelmetProvider>
      <Helmet titleTemplate="%s | JMEFit Training">
        <html lang="en" />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/favicon.ico" />
      </Helmet>
          <ErrorBoundary>
            <SkipLink />
            <ScrollToTop />
            <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
              <Navigation />
            {/* Added padding-top to ensure content isn't hidden behind the fixed header */}
            <div id="main-content" className="pt-20">
            <Suspense fallback={
              <div className="flex items-center justify-center min-h-[60vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500" />
              </div>
            }>
            <AnimatePresence mode="wait">
              <Routes location={location}>
                <Route path="/" element={<PageTransition><Home /></PageTransition>} />
                <Route path="/onboarding" element={<PageTransition><Onboarding /></PageTransition>} />
                <Route path="/nutrition-programs" element={<PageTransition><NutritionPrograms /></PageTransition>} />
                {/* ProductSelector route removed */}
                <Route path="/programs" element={<PageTransition><Programs /></PageTransition>} />
                <Route path="/monthly-app" element={<PageTransition><MonthlyApp /></PageTransition>} />
                <Route path="/standalone-programs" element={<PageTransition><StandalonePrograms /></PageTransition>} />
                <Route path="/shop" element={<PageTransition><Shop /></PageTransition>} />
                <Route path="/community" element={<PageTransition><Community /></PageTransition>} />
                <Route path="/shred-waitlist" element={<PageTransition><ShredWaitlist /></PageTransition>} />
                <Route path="/blog" element={<PageTransition><Blog /></PageTransition>} />
                <Route path="/blog/:slug" element={<PageTransition><BlogPost /></PageTransition>} />
                <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />
                <Route path="/terms" element={<PageTransition><Terms /></PageTransition>} />
                <Route path="/privacy" element={<PageTransition><Privacy /></PageTransition>} />
                <Route path="/subscribe" element={<PageTransition><Subscribe /></PageTransition>} />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <PageTransition><Dashboard /></PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/admin" element={
                <ProtectedRoute adminOnly>
                  <PageTransition><AdminDashboard /></PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/admin/login" element={<PageTransition><AdminLogin /></PageTransition>} />
              <Route path="/checkout" element={<PageTransition><Checkout /></PageTransition>} />
              <Route path="/checkout/success" element={<PageTransition><CheckoutSuccess /></PageTransition>} />
              <Route path="/redeem" element={<PageTransition><RedeemGift /></PageTransition>} />
              <Route path="/auth" element={<PageTransition><Auth /></PageTransition>} />
              <Route path="/auth/callback" element={<PageTransition><AuthCallback /></PageTransition>} />
              <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
            </Routes>
            </AnimatePresence>
            </Suspense>
            </div>
            <Footer />
            <Suspense fallback={null}><AbandonedCartBanner /></Suspense>
            <OpenAIChatWidget />
          </div>
        </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
